import { useAppSearchParams } from "@/routing/navigation";
import type {
  HeadCell,
  Order,
  RowInterface,
} from "@/types/component/TableTypes";
import type {
  ReferralChannelType,
  ReferralConfigType,
  ReferralCreditsCollectionType,
  ReferralEarnoutCollectionType,
  ReferralEarnoutType,
} from "@/types/ReferralObjectTypes.d";

import React, { type JSX } from "react";
import validate from "validate.js";
import { useCommittedLocation } from "@/routing/committedLocation";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { StyledCardv2 } from "@/components/plan/styles";
import PaginatedListTable from "@/components/tables/PaginatedListTable";
import { getOrder } from "@/components/proxyList/proxyListHelper";
import useQueryParamStates, { parseBase64 } from "@/hooks/useQueryParamState";
import TimezoneUtils from "@/utils/TimezoneUtils";
import useSnackbar from "@/hooks/useSnackbar";
import { SWRResponse } from "swr";
import useSWR from "@/hooks/useSWR";
import { REFERRAL_CHANNELS_URL } from "@/api/referral";
import {
  Accordion,
  AccordionDetails,
  Alert,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
  Link as MuiLink,
  LinearProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { nextEarnOutDate } from "@/utils/timeUtils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import RichTooltip from "../common/RichTooltip";
import { userFormatCurrency } from "@/utils/textUtils";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Highlighter, HighlighterSeverity } from "@/ui/Highlighter";
import useIntercom from "@/hooks/useIntercom";
import palette_Light_tokens from "../../designTokens/palette.Light.tokens.json";
import palette_Dark_tokens from "../../designTokens/palette.Dark.tokens.json";
import material_colors_tokens from "../../designTokens/material_colors.Value.tokens.json";

type EarningHistoryRowType = RowInterface & {
  created_at: string;
  amount: string;
  type: "PayPal" | "Credit";
  status: React.ReactNode | JSX.Element | string | undefined;
};

type EarningHistoryStatusType =
  | "processing"
  | "completed"
  | "error"
  | "failed"
  | "pending"
  | "available"
  | "reverted";

type MuiChipColorType = Record<string, string | number>;

type ReferralHistoryRowType = RowInterface & {
  created_at: string;
  amount: string;
  user_id: string;
  rowId: string;
  status: React.ReactNode | JSX.Element | string | undefined;
};

type TablePaginatorPropType = {
  rowsPerPage: [number, React.Dispatch<React.SetStateAction<number>>, string];
  page: [number, React.Dispatch<React.SetStateAction<number>>, string];
  order: [Order, React.Dispatch<React.SetStateAction<Order>>, string];
  orderBy: [
    string | null,
    React.Dispatch<React.SetStateAction<string | null>>,
    string,
  ];
};

type ReferralContextType = {
  creditsQuery: URLSearchParams;
  setCreditsQuery: React.Dispatch<React.SetStateAction<URLSearchParams>>;
  earnoutQuery: URLSearchParams;
  setEarnoutQuery: React.Dispatch<React.SetStateAction<URLSearchParams>>;
  earnouts: TablePaginatorPropType;
  referrals: TablePaginatorPropType;
};

export const ReferralFilterContext = React.createContext(
  {} as ReferralContextType
);

const filterDefault: ReferralTableFilterType = {
  page: 0,
  order: "desc",
  orderBy: "created_at",
  rowsPerPage: 10,
};

export const ReferallFilterProvider: React.FC<{
  config: ReferralConfigType;
  children: React.ReactNode;
}> = ({ config, children }) => {
  const searchParams = useAppSearchParams();
  const query = Object.fromEntries(searchParams);

  const [creditsQuery, setCreditsQuery] = React.useState<URLSearchParams>(
    new URLSearchParams("page_size=10&ordering=-created_at")
  );

  const [earnoutQuery, setEarnoutQuery] = React.useState<URLSearchParams>(
    new URLSearchParams("page_size=10&mode=payout&ordering=-created_at")
  );

  const earnoutQs = React.useMemo((): ReferralTableFilterType => {
    if ("earnouts" in query) {
      const params = parseBase64(String(searchParams.get("earnouts")));
      return {
        ...filterDefault,
        ...params,
      };
    }
    return filterDefault;
  }, [query, searchParams]);

  const referralQs = React.useMemo((): ReferralTableFilterType => {
    if ("referrals" in query) {
      const params = parseBase64(String(searchParams.get("referrals")));
      return {
        ...filterDefault,
        ...params,
      };
    }
    return filterDefault;
  }, [query, searchParams]);

  const earnouts = useQueryParamStates<ReferralTableFilterType>(
    earnoutQs,
    "earnouts"
  );

  const referrals = useQueryParamStates<ReferralTableFilterType>(
    referralQs,
    "referrals"
  );

  const earnoutPage = earnouts.page[0];
  const earnoutRowsPerPage = earnouts.rowsPerPage[0];
  const earnoutOrder = earnouts.order[0];
  const earnoutOrderBy = earnouts.orderBy[0];

  React.useEffect(() => {
    if (!config) return;

    setEarnoutQuery((prev) => {
      const searchParams = new URLSearchParams(prev.toString());
      searchParams.set("page", String(earnoutPage + 1));
      searchParams.set("page_size", String(earnoutRowsPerPage));
      searchParams.set("type", config.mode);
      searchParams.set("order", earnoutOrder);
      searchParams.set(
        "ordering",
        String(getOrder(earnoutOrder, earnoutOrderBy))
      );

      return searchParams;
    });
  }, [earnoutPage, earnoutRowsPerPage, earnoutOrder, earnoutOrderBy, config]);

  const referralPage = referrals.page[0];
  const referralRowsPerPage = referrals.rowsPerPage[0];
  const referralOrder = referrals.order[0];
  const referralOrderBy = referrals.orderBy[0];

  React.useEffect(() => {
    if (!config) return;

    setCreditsQuery((prev) => {
      const searchParams = new URLSearchParams(prev.toString());
      searchParams.set("page", String(referralPage + 1));
      searchParams.set("page_size", String(referralRowsPerPage));
      searchParams.set("order", referralOrder);
      searchParams.set(
        "ordering",
        String(getOrder(referralOrder, referralOrderBy))
      );

      return searchParams;
    });
  }, [
    referralPage,
    referralRowsPerPage,
    referralOrder,
    referralOrderBy,
    config,
  ]);

  return (
    <ReferralFilterContext.Provider
      value={{
        creditsQuery,
        setCreditsQuery,
        earnoutQuery,
        setEarnoutQuery,
        earnouts: {
          rowsPerPage: earnouts.rowsPerPage,
          page: earnouts.page,
          order: earnouts.order,
          orderBy: earnouts.orderBy,
        },
        referrals: {
          rowsPerPage: referrals.rowsPerPage,
          page: referrals.page,
          order: referrals.order,
          orderBy: referrals.orderBy,
        },
      }}
    >
      {children}
    </ReferralFilterContext.Provider>
  );
};

export function useReferralConfig(): Pick<
  SWRResponse<ReferralConfigType>,
  "data" | "mutate" | "isValidating"
> {
  const { data, mutate, isValidating } = useSWR<ReferralConfigType>(
    "/referral/config/",
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateIfStale: true,
    }
  );

  return {
    data,
    mutate,
    isValidating,
  };
}

const PaypalLogo: React.FC<{ width?: number; height?: number }> = ({
  width = 75,
  height = 16,
}) => {
  const { palette } = useTheme();
  const colors = React.useMemo((): Record<"primary" | "secondary", string> => {
    const darkColors = {
      primary: palette.grey[600],
      secondary: palette.common.white,
    };

    if (!palette || palette.mode === "dark") {
      return darkColors;
    }

    return {
      primary: palette.paypal?.blueLight,
      secondary: palette.paypal?.blue,
    };
  }, [palette]);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        justifyItems: "center",
        alignItems: "center",
        lineHeight: 1,
        height: "auto",
        marginRight: 2,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        id="Layer_1"
        x="0"
        y="0"
        version="1.1"
        width={`${width}px`}
        height={`${height}px`}
        viewBox="0 0 122.88 29.77"
      >
        <path
          fill={colors.primary}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.72 7.01h8.4c4.51 0 6.21 2.28 5.94 5.64-.43 5.54-3.78 8.6-8.22 8.6H14.6c-.61 0-1.02.4-1.18 1.5l-.95 6.35c-.06.41-.28.65-.6.68H6.59c-.5 0-.67-.38-.54-1.2L9.26 8.21c.13-.81.57-1.2 1.46-1.2z"
        />
        <path
          fill={colors.secondary}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M47.18 6.63c2.83 0 5.45 1.54 5.09 5.37-.43 4.55-2.87 7.07-6.72 7.08h-3.36c-.48 0-.72.39-.84 1.2l-.65 4.13c-.1.62-.42.93-.89.93h-3.13c-.5 0-.67-.32-.56-1.03L38.7 7.74c.13-.82.43-1.12.99-1.12h7.49v.01zm-5.1 8.87h2.55c1.59-.06 2.65-1.16 2.76-3.15.06-1.23-.77-2.11-2.09-2.1l-2.4.01-.82 5.24zm18.69 8.58c.29-.26.58-.39.54-.07l-.1.77c-.05.4.11.61.48.61h2.78c.47 0 .7-.19.81-.91l1.71-10.75c.09-.54-.05-.8-.46-.8h-3.06c-.28 0-.41.15-.48.57l-.11.66c-.06.34-.22.41-.36.06-.52-1.23-1.84-1.78-3.69-1.73-4.29.09-7.17 3.34-7.48 7.51-.24 3.23 2.07 5.76 5.12 5.76 2.21-.01 3.2-.66 4.3-1.68zm-2.33-1.65c-1.84 0-3.13-1.47-2.86-3.28.27-1.8 1.99-3.28 3.84-3.28s3.13 1.47 2.86 3.28c-.27 1.8-1.99 3.28-3.84 3.28zm13.98-9.54H69.6c-.58 0-.82.43-.63.97l3.5 10.24-3.43 4.87c-.29.41-.06.78.34.78h3.17c.04 0 .07.01.11.01.34 0 .65-.18.83-.47l10.76-15.43c.33-.47.18-.97-.37-.97h-3c-.51 0-.72.2-1.01.63l-4.49 6.5-2-6.52c-.12-.4-.41-.61-.96-.61z"
        />
        <path
          fill={colors.primary}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M95.44 6.63c2.83 0 5.45 1.54 5.09 5.37-.43 4.55-2.87 7.07-6.72 7.08h-3.36c-.48 0-.72.39-.84 1.2l-.65 4.13c-.1.62-.42.93-.89.93h-3.13c-.5 0-.67-.32-.56-1.03l2.59-16.57c.13-.81.43-1.12.99-1.12l7.48.01zm-5.09 8.87h2.55c1.59-.06 2.65-1.16 2.76-3.15.06-1.23-.77-2.11-2.09-2.1l-2.4.01-.82 5.24zm18.68 8.58c.29-.26.58-.39.54-.07l-.1.77c-.05.4.11.61.48.61h2.78c.47 0 .7-.19.81-.91l1.71-10.75c.09-.54-.05-.8-.46-.8h-3.05c-.28 0-.41.15-.48.57l-.11.66c-.06.34-.22.41-.36.06-.52-1.23-1.84-1.78-3.68-1.73-4.29.09-7.17 3.34-7.48 7.51-.24 3.23 2.07 5.76 5.12 5.76 2.21 0 3.2-.65 4.31-1.67l-.03-.01zm-2.32-1.65c-1.84 0-3.13-1.47-2.86-3.28.27-1.8 1.99-3.28 3.84-3.28 1.84 0 3.13 1.47 2.86 3.28-.27 1.8-2 3.28-3.84 3.28zm12.81 2.97h-3.23a.38.38 0 0 1-.38-.38v-.05l2.82-17.85c.06-.25.28-.43.53-.43h3.23c.21 0 .38.17.38.38v.05l-2.82 17.85c-.05.25-.27.43-.53.43z"
        />
        <path
          fill={colors.secondary}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.47 0h8.41c2.37 0 5.18.08 7.05 1.73 1.25 1.11 1.91 2.87 1.76 4.77-.52 6.42-4.35 10.01-9.5 10.01H9.04c-.71 0-1.17.47-1.37 1.73l-1.16 7.37c-.08.48-.28.76-.65.79H.68c-.57 0-.78-.43-.63-1.39L3.78 1.4C3.93.45 4.45 0 5.47 0z"
        />
        <path
          fill={colors.secondary}
          fillRule="evenodd"
          clipRule="evenodd"
          d="m7.79 17.5 1.47-9.29C9.39 7.4 9.83 7 10.72 7h8.4c1.39 0 2.51.22 3.39.62-.84 5.71-4.54 8.89-9.38 8.89H9c-.56 0-.96.28-1.21.99z"
        />
      </svg>
    </Box>
  );
};

const CopyToClipboardFeedback: React.FC = React.memo(
  function CopyToClipboardMemo() {
    return (
      <Box display="flex" justifyContent="flex-start" alignItems="center">
        <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
        <span>Copied to Clipboard</span>
      </Box>
    );
  }
);

const PrivacyPolicyContent: React.FC<{
  onClose: () => void;
}> = React.memo(function PrivacyPolicyMemo({ onClose }) {
  return (
    <Box px={4}>
      <Typography component="div" variant="body2">
        {`Before sharing your referral link on your social media channels, emails,
        websites, or any other channels you're using, please ensure that the
        following steps are taken:`}
      </Typography>
      <Box component="ol" px={3}>
        <Typography component="li" variant="body2" mb={2}>
          {`Webshare referrers/affiliates that take part in Pay-Per-Click
          advertisement campaigns must not bid on the keyword "webshare" and add
          it to their exclusions list, regardless of the PPC platform used. This
          also includes any other similar phrases that contain "webshare" as
          part of the keyword.`}
        </Typography>

        <Typography component="li" variant="body2" mb={2}>
          If promoting on social media, you should include disclosures at the
          beginning of your content. For example <code>&#35;sponsored</code>{" "}
          tag.
        </Typography>
        <Typography component="li" variant="body2" mb={2}>
          Do not self-benefit from your own referral/affiliate link &#40;e.g.
          create multiple accounts owned by yourself&#41;.
        </Typography>
        <Typography component="li" variant="body2" mb={2}>
          It is strongly advised to read our full{" "}
          <MuiLink
            underline="hover"
            color="primary.main"
            target="_blank"
            href="https://www.webshare.io/affiliate-agreement"
          >
            affiliate agreement.
          </MuiLink>{" "}
          before taking part in active promotional campaigns.
        </Typography>
      </Box>
      <Typography component="div" variant="body2">
        {`Affiliates/referrers that violate terms stated in the agreement may be
        contacted and potentially asked to provide details on their promotional
        activities in regards to Webshare. More serious cases of violating
        sensitive terms may result in a evaluation of the affiliate's
        accumulated earnings and/or a potential suspension of their referral
        account.`}
      </Typography>
      <Box py={4} textAlign="center">
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          date-testid="referral-policy-dismiss"
        >
          Close
        </Button>
      </Box>
    </Box>
  );
});

const referralInfo = [
  "30 days after a referred user completes their purchase,",
  "you will be eligible for the reward. During this time,",
  "if the payment is reverted or the account is suspended,",
  "you will not be eligible for a reward.",
].join(" ");

export const RewardBalanceCard: React.FC<{
  config: ReferralConfigType;
  children?: React.ReactNode;
}> = ({ config, children }) => {
  const progress = React.useMemo((): {
    text: string;
    value: number;
    currency: string;
    balance: string;
  } => {
    const earned =
      config.mode === "credits"
        ? Number(config.credits_earned)
        : Number(config.payouts_earned);
    const percentage = (earned / config.minimum_earn_out_amount) * 100;
    const display = percentage > 100 ? 100 : percentage;
    return {
      text: `${display}%`,
      value: Number(display),
      currency: config.minimum_earn_out_amount.toLocaleString("en-us", {
        style: "currency",
        currency: "USD",
      }),
      balance: userFormatCurrency(earned),
    };
  }, [config]);

  return (
    <StyledCardv2
      sx={{
        flexGrow: 1,
        minHeight: {
          xs: "auto",
          lg: "100%",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{
          height: "100%",
          minHeight: {
            xs: "auto",
            lg: 100,
          },
        }}
      >
        <Grid container alignItems="be" minHeight={80} size={12}>
          <Grid size={12}>
            <Typography
              component="div"
              variant={"subtitle1"}
              color={"text.primary"}
              sx={{ pb: 1 }}
            >
              {config.mode === "credits"
                ? "⏳ Credit pending"
                : "⏳ Commission pending"}{" "}
              <Tooltip
                title={
                  config.mode === "credits"
                    ? "Credits waiting to be applied after the holding period."
                    : "Commission earned and waiting to be applied after the holding period."
                }
                arrow
              >
                <InfoOutlined
                  sx={{
                    fontSize: 16,
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Typography>
            <Typography component="div" variant="h5" data-testid="balance">
              {progress.balance}
            </Typography>
          </Grid>
          <Grid size={12}>{children}</Grid>
        </Grid>

        {config.mode === "payout" && (
          <Grid container alignItems="center" size={12}>
            <Grid sx={{ pr: 1 }} size={11}>
              <LinearProgress
                data-testid="progress-bar"
                variant="determinate"
                value={progress.value}
              />
            </Grid>
            <Grid textAlign="right" sx={{ pl: 1 }} size={1}>
              <Typography variant="body2" data-testid="progress-percentage">
                {progress.text}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption">
                <span>Earn {progress.currency}</span>{" "}
                <span>minimum to receive a payout.</span>
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" fontWeight="bold">
                  {Number(config.payouts_earned).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </Typography>
                <Typography variant="caption" component="span" sx={{ px: 1 }}>
                  {nextEarnOutDate(
                    TimezoneUtils.zonedDate(Date.now()),
                    TimezoneUtils.zonedDate(new Date(config.next_earn_out_date))
                  )}
                </Typography>

                <Box
                  sx={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mx: 1,
                  }}
                >
                  <RichTooltip title={referralInfo}>
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </RichTooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {config.mode === "credits" &&
        TimezoneUtils.zonedDate(Date.now()) <=
          TimezoneUtils.zonedDate(new Date(config.next_earn_out_date)) ? (
          <Box sx={{ pb: 0, mb: { lg: -4 } }}>
            <Typography variant="caption" data-testid="next-earnout">
              <span>Next Earnout: </span>
              <span>
                {String(
                  TimezoneUtils.zonedDate(
                    config.next_earn_out_date
                  ).toDateString()
                )}
              </span>
            </Typography>
          </Box>
        ) : null}
      </Box>
    </StyledCardv2>
  );
};
export const EarningsCard: React.FC<{
  mode?: "credits" | "payout";
  upgraded: number;
  referred: number;
  total: number;
}> = ({ mode, upgraded = 0, referred = 0, total = 0 }) => {
  return (
    <StyledCardv2
      sx={{
        flexGrow: 1,
        padding: 3,
        minHeight: {
          lg: "100%",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <Grid container minHeight={80} size={12}>
          <Grid size={12}>
            <Typography
              component="div"
              variant="subtitle1"
              color="text.primary"
              sx={{ pb: 1 }}
            >
              {mode === "credits"
                ? "💰 Credit applied"
                : "💰 Commission applied"}{" "}
              {mode ? (
                <Tooltip
                  title={
                    mode === "credits"
                      ? "Credits that have been applied to your account over time."
                      : "Commission that has been applied to your account over time."
                  }
                  arrow
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              ) : null}
            </Typography>
            <Typography
              component="div"
              variant="h5"
              data-testid="earnings-total"
            >
              {userFormatCurrency(total)}
            </Typography>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={3}>
          <Box>
            <Typography component="div" variant="caption">
              <span data-testid="referred-count">{referred}</span>{" "}
              {referred > 1 ? <span>users</span> : <span>user</span>} referred
            </Typography>
          </Box>
          <Box>
            <Typography component="div" variant="caption">
              <span data-testid="referred-upgraded">{upgraded}</span>{" "}
              {upgraded > 1 ? <span>users</span> : <span>user</span>} upgraded
            </Typography>
          </Box>
        </Stack>
      </Box>
    </StyledCardv2>
  );
};

export const ReferralRateCards: React.FC<{
  config: ReferralConfigType;
}> = ({ config }) => {
  const initialRate = config.initial_rate;
  const ongoingRate = config.ongoing_rate;
  const initialPeriodDays = config.initial_rate_period_days;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        gap: 1,
        mt: 3,
      }}
    >
      <Box
        sx={{
          flex: 1,
          py: 2,
          px: 3,
          bgcolor: "grey.100",
          borderRadius: 1,
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          First {initialPeriodDays} days
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Earn {Math.round(initialRate * 100)}% of your referral&apos;s spend
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 0, md: 1 },
        }}
      >
        <ArrowForwardIcon
          sx={{ display: { xs: "none", md: "block" }, fontSize: 20 }}
          color="action"
        />
        <KeyboardArrowDownIcon
          sx={{ display: { xs: "block", md: "none" }, fontSize: 20 }}
          color="action"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          py: 2,
          px: 3,
          bgcolor: "grey.100",
          borderRadius: 1,
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          Ongoing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Earn {Math.round(ongoingRate * 100)}% after that
        </Typography>
      </Box>
    </Box>
  );
};

export const PrivacyPolicy = () => {
  const [showDialog, setShowDialog] = React.useState(false);
  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Bidding on keywords containing the brand name through PPC systems is
        prohibited. For more details on specific restrictions and good practices
        for affiliates,&nbsp;
      </Typography>
      <MuiLink
        component="button"
        onClick={() => setShowDialog(true)}
        data-testid="referral-policy-link"
      >
        <Typography variant="caption" color="primary.main">
          read our policy
        </Typography>
      </MuiLink>

      <Typography variant="caption" color="text.secondary">
        &nbsp;before sharing.
      </Typography>
      <Dialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        data-testid="referral-policy-modal"
      >
        <DialogTitle>Privacy Policy</DialogTitle>
        <PrivacyPolicyContent onClose={() => setShowDialog(false)} />
      </Dialog>
    </>
  );
};

export const ReferralLinkCard: React.FC<{
  link: string;
  mode?: "credits" | "payout";
}> = ({ link, mode }) => {
  const { palette } = useTheme();
  const { setFeedback } = useSnackbar();
  const linkRef = React.useRef<HTMLInputElement | undefined>(undefined);

  const bgcolor = React.useMemo(
    () => (palette.mode === "light" ? "primary.main" : "primary.light"),
    [palette]
  );

  const color = React.useMemo(
    () => (palette.mode === "light" ? "common.white" : "common.black"),
    [palette]
  );

  const handleCopy = React.useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (!linkRef.current) {
        return;
      }

      if (navigator && navigator?.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(linkRef.current.value)
          .then(() =>
            setFeedback({
              open: true,
              type: "default",
              message: <CopyToClipboardFeedback />,
            })
          )
          .catch(() => console.error("Failed to copy URL to clipboard"));
      }
    },
    [setFeedback]
  );

  return (
    <StyledCardv2
      sx={{
        flexGrow: 1,
        padding: 3,
        minHeight: {
          lg: "100%",
        },
      }}
    >
      <Typography variant={"subtitle2"} component="div" color={"text.primary"}>
        {mode === "credits"
          ? "Your personal refer a friend link"
          : "Your personal affiliate link"}
      </Typography>
      <Box
        my={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TextField
          inputRef={linkRef}
          fullWidth
          value={link}
          size="small"
          disabled
          inputProps={{
            "data-testid": "referral-link-input",
          }}
        />
        <Button
          disableElevation
          data-testid="copy-button"
          onClick={handleCopy}
          variant="contained"
          sx={{ marginLeft: 3, color, bgcolor }}
        >
          Copy
        </Button>
      </Box>
      <Box />
    </StyledCardv2>
  );
};

export const PayoutEmailDialog: React.FC<{
  config: ReferralConfigType;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onOpen?: () => void;
  onClose?: () => void;
  handleUpdate: (email: string) => void;
}> = ({ config, showDialog, setShowDialog, onClose, handleUpdate }) => {
  const location = useCommittedLocation();

  const [paypalEmail, setPaypalEmail] = React.useState(
    config.paypal_payout_email ?? ""
  );
  const [emailError, setEmailError] = React.useState<string | null>(null);

  const dialogTitle = React.useMemo((): Record<"state" | "title", string> => {
    const current = new URL(location.href, window.location.origin);
    const paypalEmail = current.searchParams.get("email");

    if (paypalEmail && paypalEmail === "confirm") {
      return {
        state: "confirm",
        title: "Confirm your PayPal account",
      };
    }

    if (paypalEmail && paypalEmail === "connect") {
      return {
        state: "connect",
        title: "Confirm your PayPal account",
      };
    }

    return {
      state: "update",
      title: "Change your PayPal Account",
    };
  }, [location]);

  const handleClose = () => {
    onClose && onClose();
    setShowDialog(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationResult = validate(
      { paypalEmail },
      {
        paypalEmail: {
          presence: true,
          email: true,
        },
      }
    );

    if (validationResult && validationResult.paypalEmail) {
      return setEmailError(validationResult.paypalEmail);
    }

    handleUpdate(paypalEmail);
  };

  return (
    <Dialog
      open={showDialog}
      onClose={handleClose}
      data-testid="email-dialog"
      slotProps={{
        paper: {
          sx: {
            m: { xs: 3, sm: 5 },
            width: { xs: "calc(100% - 32px)", sm: "auto" },
            maxWidth: { xs: "calc(100% - 32px)", sm: 600 },
          },
        },
      }}
    >
      <DialogTitle>{dialogTitle.title}</DialogTitle>
      <Box sx={{ minWidth: { sm: 480 }, pt: 0, pb: 4, px: 4 }}>
        {(dialogTitle.state === "confirm" ||
          dialogTitle.state === "connect") && (
          <>
            <Typography
              component="div"
              variant="body1"
              color="text.secondary"
              sx={{ pb: 3 }}
            >
              PayPal will be used for affiliate cash payouts.
            </Typography>
            <Box component="ul" sx={{ pl: 3, my: 0, pb: 3 }}>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Identity verification is required before withdrawing funds.
              </Typography>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                You can complete verification later from your dashboard when
                you&apos;re ready.
              </Typography>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Your existing Refer-a-Friend credits are not affected.
              </Typography>
            </Box>
            <SanctionsNotice />
          </>
        )}
        <FormControl fullWidth component="form" onSubmit={handleSubmit}>
          <TextField
            label="Paypal Email"
            value={paypalEmail}
            onChange={(e) => {
              setEmailError(null);
              setPaypalEmail(e.target.value);
            }}
            inputProps={{ "data-testid": "paypal-email-input" }}
            InputProps={{
              startAdornment: <PaypalLogo />,
            }}
            error={!!emailError}
            helperText={emailError}
          />
          <Typography sx={{ mt: 1 }} variant="caption" color="text.secondary">
            By becoming Webshare Affiliate, you agree to the{" "}
            <MuiLink
              href="https://www.webshare.io/affiliate-agreement"
              underline="hover"
              color="primary.main"
            >
              affiliate agreement.
            </MuiLink>
          </Typography>

          <Box display="flex" justifyContent="flex-end" sx={{ mt: 4, pt: 4 }}>
            <Button
              type="button"
              variant="text"
              sx={{ mr: 4 }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              data-testid="confirm-button"
            >
              Confirm
            </Button>
          </Box>
        </FormControl>
      </Box>
    </Dialog>
  );
};

// The theme's info palette is not the one Figma uses for alerts, so the
// notice takes its colours from the design tokens directly.
const SANCTIONS_NOTICE_COLORS = {
  light: {
    ...palette_Light_tokens.components.alert.info,
    icon: material_colors_tokens.lightBlue["700"],
  },
  dark: {
    ...palette_Dark_tokens.components.alert.info,
    icon: material_colors_tokens.lightBlue["200"],
  },
};

const SanctionsNotice = () => {
  const { showChat } = useIntercom();
  const theme = useTheme();
  const colors = SANCTIONS_NOTICE_COLORS[theme.palette.mode];

  return (
    <Alert
      severity="info"
      variant="standard"
      icon={
        <InfoOutlined fontSize="inherit" sx={{ color: colors.icon.value }} />
      }
      sx={{
        py: 1,
        px: 2,
        mb: { xs: 3, sm: 4 },
        color: colors.color.value,
        bgcolor: colors.background.value,
      }}
      slotProps={{
        icon: { sx: { py: 1, pr: 2, pl: 0, mr: 0 } },
        message: { sx: { py: 1 } },
      }}
    >
      Sanctions prevent affiliate payouts and ID verification in some countries.{" "}
      <MuiLink
        component="button"
        type="button"
        color="inherit"
        underline="always"
        onClick={showChat}
        sx={{ verticalAlign: "baseline" }}
      >
        Contact support
      </MuiLink>{" "}
      if this may apply to you.
    </Alert>
  );
};

export const ReferralPaypalCard: React.FC<{
  config: ReferralConfigType;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onOpen?: () => void;
  onClose?: () => void;
  handleUpdate: (email: string) => void;
}> = ({ config, showDialog, setShowDialog, handleUpdate, onOpen, onClose }) => {
  const handleOpen = () => {
    onOpen && onOpen();
    setShowDialog(true);
  };

  return (
    <StyledCardv2
      sx={{
        flexGrow: 1,
        padding: 3,
        minHeight: {
          lg: "100%",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      ></Box>
      <Box sx={{ pt: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <PaypalLogo />
            <Typography
              mt={1}
              variant="body2"
              component="div"
              color="text.primary"
              data-testid="paypal-email"
            >
              {!config.paypal_payout_email ||
              config.paypal_payout_email.trim() == "" ? (
                <span>Please connect your paypal account</span>
              ) : (
                <span>{config.paypal_payout_email}</span>
              )}
            </Typography>
          </Box>

          <Button
            data-testid="paypal-connect-button"
            variant="text"
            color="primary"
            size="small"
            sx={{ mr: -1 }}
            onClick={handleOpen}
          >
            Edit
          </Button>
        </Stack>
      </Box>

      <PayoutEmailDialog
        config={config}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onOpen={onOpen}
        onClose={onClose}
        handleUpdate={handleUpdate}
      />
    </StyledCardv2>
  );
};

type ReferralTablePropType = {
  children?: React.ReactNode;
  paging:
    | Omit<ReferralEarnoutCollectionType, "results">
    | Omit<ReferralCreditsCollectionType, "results">
    | undefined;
  columns: Array<HeadCell>;
  rows: Array<EarningHistoryRowType | ReferralHistoryRowType>;
  filterName: string;
  noDataText?: string;
};

type ReferralTableFilterType = {
  rowsPerPage: number;
  page: number;
  order: Order;
  orderBy: string | null;
};

const RefererralTable: React.FC<ReferralTablePropType> = ({
  children,
  columns,
  rows,
  paging,
  filterName,
  noDataText,
}) => {
  const context = React.useContext(ReferralFilterContext);
  const paginator = context[
    filterName as "earnouts" | "referrals"
  ] as TablePaginatorPropType;

  const [_rowsPerPage, setRowsPerPage] = paginator.rowsPerPage;
  const [_page, setPage] = paginator.page;
  const [_order, setOrder] = paginator.order;
  const [_orderBy, setOrderBy] = paginator.orderBy;
  return (
    <PaginatedListTable
      selectionColumn={false}
      headCells={columns}
      rows={rows}
      rowsPerPage={_rowsPerPage}
      setRowsPerPage={setRowsPerPage}
      page={_page}
      setPage={setPage}
      order={_order}
      setOrder={setOrder}
      orderBy={_orderBy}
      setOrderBy={setOrderBy}
      info={children}
      tableSx={{ minWidth: "100%" }}
      pagination={paging}
      rowsPerPageOptions={[10, 25, 50, 75, 100]}
      noDataText={noDataText}
    />
  );
};

const StatusComponent: React.FC<{
  status: EarningHistoryStatusType;
  errorReason?: null | string;
}> = ({ status, errorReason }) => {
  const customChip = React.useMemo((): MuiChipColorType => {
    switch (status) {
      case "error":
      case "reverted":
        return { borderColor: "error.main", color: "error.light" };
      case "processing":
      case "pending":
        return { borderColor: "warning.main", color: "warning.light" };
      case "available":
        return { borderColor: "info.main", color: "info.light" };
      case "completed":
        return { borderColor: "primary.main", color: "primary.light" };
      default:
        return { color: "grey.500", borderColor: "grey.800" };
    }
  }, [status]);

  return (
    <RichTooltip title={errorReason ?? undefined}>
      <Chip
        sx={{ textTransform: "capitalize", minWidth: 70, ...customChip }}
        label={status}
        color="default"
        variant="outlined"
        size="small"
      />
    </RichTooltip>
  );
};

export const ReferralEarningHistory: React.FC<{
  earnouts: ReferralEarnoutCollectionType | undefined;
}> = ({ earnouts }) => {
  const columns: Array<HeadCell> = [
    {
      id: "created_at",
      label: "Date",
      disablePadding: false,
    },
    {
      id: "type",
      label: "Type",
      disablePadding: false,
      disableOrdering: true,
    },
    {
      id: "status",
      label: "Status",
      disablePadding: false,
    },
    {
      id: "amount",
      label: "Amount",
      disablePadding: false,
      numeric: true,
    },
  ];

  const rows = React.useMemo((): Array<EarningHistoryRowType> => {
    if (!earnouts || !earnouts.results) {
      return [];
    }

    return earnouts.results.map(
      (item: ReferralEarnoutType): EarningHistoryRowType => ({
        rowId: String(item.id),
        created_at: TimezoneUtils.zonedDate(item.created_at).toDateString(),
        type: item.type === "payout" ? "PayPal" : "Credit",
        status: (
          <StatusComponent
            status={item.status}
            errorReason={item.error_reason}
          />
        ),
        amount: item.amount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
      })
    );
  }, [earnouts]);

  return (
    <RefererralTable
      paging={earnouts}
      columns={columns}
      rows={rows}
      filterName="earnouts"
      noDataText="Earnings will appear here once they’re applied to your account"
    />
  );
};

export const ReferredUsers: React.FC<{
  credits: ReferralCreditsCollectionType | undefined;
}> = ({ credits }) => {
  const columns: Array<HeadCell> = [
    {
      id: "user_id",
      label: "User #",
      disablePadding: false,
    },
    {
      id: "created_at",
      label: "Credit Date",
      disablePadding: false,
      width: "180px",
    },
    {
      id: "status",
      label: "Status",
      disablePadding: false,
    },
    {
      label: "Amount",
      id: "amount",
      disablePadding: false,
      width: "100px",
      numeric: true,
    },
  ];

  const rows = React.useMemo(() => {
    if (!credits || !credits.results) {
      return [];
    }

    return credits.results.map(
      (i): ReferralHistoryRowType => ({
        rowId: String(i.id),
        user_id: ["User", String(i.user_id)].join("-"),
        created_at: TimezoneUtils.zonedDate(i.created_at).toDateString(),
        status: <StatusComponent status={i.status} />,
        amount: userFormatCurrency(i.amount),
      })
    );
  }, [credits]);

  return (
    <RefererralTable
      paging={credits}
      columns={columns}
      rows={rows}
      filterName="referrals"
      noDataText="Users you refer will appear here once they sign up"
    />
  );
};

type CouponStatus = "active" | "inactive" | "expired";

type CouponRowType = RowInterface & {
  code: React.ReactNode;
  uses: string;
  revenue: string;
  commission: string;
  status: React.ReactNode;
};

const COUPON_STATUS_COLOR: Record<CouponStatus, HighlighterSeverity> = {
  active: "primary",
  inactive: "default",
  expired: "warning",
};

function getCouponStatus(channel: ReferralChannelType): CouponStatus {
  if (channel.end_date && new Date(channel.end_date).getTime() <= Date.now()) {
    return "expired";
  }
  if (!channel.is_active) return "inactive";
  return "active";
}

const COUPON_SORT_KEYS: Record<string, (c: ReferralChannelType) => number> = {
  uses: (c) => c.total_uses,
  revenue: (c) => c.total_revenue ?? 0,
  commission: (c) => c.total_commission,
};

function sortChannels(
  channels: ReferralChannelType[],
  orderBy: string | null,
  order: Order
): ReferralChannelType[] {
  if (!orderBy) return channels;
  const getValue = COUPON_SORT_KEYS[orderBy];
  if (!getValue) return channels;
  const direction = order === "asc" ? 1 : -1;
  return [...channels].sort((a, b) => (getValue(a) - getValue(b)) * direction);
}

const CouponStatusChip: React.FC<{ status: CouponStatus }> = ({ status }) => (
  <Highlighter
    innerVariant="opacity"
    severity={COUPON_STATUS_COLOR[status]}
    size="medium"
  >
    {status.toUpperCase()}
  </Highlighter>
);

const COUPON_COLUMNS: HeadCell[] = [
  { id: "code", label: "Code", disablePadding: false, disableOrdering: true },
  { id: "uses", label: "Uses", disablePadding: false },
  { id: "revenue", label: "Revenue", disablePadding: false },
  { id: "commission", label: "Commissions", disablePadding: false },
  {
    id: "status",
    label: "Status",
    disablePadding: false,
    disableOrdering: true,
  },
];

const couponsTableSx = {
  minWidth: "100%",
  "& .MuiTableHead-root .MuiTableCell-head": {
    backgroundColor: "background.default",
  },
  "& .MuiTableHead-root .MuiTableSortLabel-root": {
    flexDirection: "row-reverse",
    justifyContent: "flex-end",
    "& .MuiTableSortLabel-icon": {
      marginLeft: 0,
      marginRight: 0.5,
    },
  },
};

function buildCouponRow(channel: ReferralChannelType): CouponRowType {
  return {
    rowId: String(channel.id),
    code: <Typography variant="body2">{channel.code}</Typography>,
    uses: channel.total_uses.toLocaleString("en-US"),
    revenue:
      channel.total_revenue == null
        ? "—"
        : userFormatCurrency(channel.total_revenue),
    commission: userFormatCurrency(channel.total_commission),
    status: <CouponStatusChip status={getCouponStatus(channel)} />,
  };
}

export const CouponsTable: React.FC<{
  channels?: ReferralChannelType[];
}> = ({ channels }) => {
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<string | null>("revenue");

  const fetchedChannels = useSWR<ReferralChannelType[]>(
    channels === undefined ? REFERRAL_CHANNELS_URL : null,
    { revalidateOnFocus: false }
  ).data;

  const effective = channels ?? fetchedChannels;

  const rows = React.useMemo<CouponRowType[] | null>(() => {
    if (!effective) return null;
    return sortChannels(effective, orderBy, order).map(buildCouponRow);
  }, [effective, order, orderBy]);

  return (
    <PaginatedListTable
      selectionColumn={false}
      headCells={COUPON_COLUMNS}
      rows={rows}
      rowsPerPage={rows?.length ?? 10}
      setRowsPerPage={() => {}}
      page={0}
      setPage={() => {}}
      order={order}
      setOrder={setOrder}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      tableSx={couponsTableSx}
      noDataText="Coupon codes you create will appear here"
    />
  );
};

function formatExpiration(endDate: string | null): string {
  if (!endDate) return "No expiration";
  return TimezoneUtils.zonedDate(endDate).toDateString();
}

const PROXY_TYPE_LABELS: Record<string, string> = {
  datacenter: "Proxy Server",
  staticresidential: "Static Residential",
  residential: "Rotating Residential",
  verified: "Verified",
};

function formatAppliesTo(channel: ReferralChannelType): string {
  const types = channel.allowed_proxy_types ?? [];
  if (types.length === 0) return "All products";
  return types.map((t) => PROXY_TYPE_LABELS[t] ?? t).join(", ");
}

function formatCouponDiscount(channel: ReferralChannelType): string {
  const numeric = Number(channel.promo_value);
  if (channel.promo_type === "percent_off") {
    return `${Math.round(numeric * 100)}% OFF`;
  }
  return `${userFormatCurrency(numeric)} OFF`;
}

function copyToClipboard(text: string, onSuccess: () => void) {
  if (!navigator?.clipboard?.writeText) return;
  navigator.clipboard
    .writeText(text)
    .then(onSuccess)
    .catch(() => console.error("Failed to copy to clipboard"));
}

const DetailColumn: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack spacing={0.5}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle2" color="text.primary">
      {value}
    </Typography>
  </Stack>
);

const CouponAccordionSummary: React.FC<{
  channel: ReferralChannelType;
  onCopy: () => void;
}> = ({ channel, onCopy }) => (
  <AccordionSummary
    expandIcon={<ExpandMoreIcon />}
    sx={{
      px: 3,
      minHeight: 0,
      "&.Mui-expanded": { minHeight: 0 },
      "& .MuiAccordionSummary-content": {
        alignItems: "center",
        gap: 1,
        my: 1,
        "&.Mui-expanded": { my: 1 },
      },
    }}
  >
    <Typography variant="body2" color="text.primary">
      {channel.code}
    </Typography>
    <IconButton
      size="small"
      aria-label={`Copy coupon code ${channel.code}`}
      onClick={(event) => {
        event.stopPropagation();
        onCopy();
      }}
    >
      <ContentCopyIcon fontSize="small" />
    </IconButton>
    <Box flexGrow={1} />
    <Typography variant="body2" color="text.secondary">
      {formatCouponDiscount(channel)}
    </Typography>
  </AccordionSummary>
);

const CouponAccordion: React.FC<{ channel: ReferralChannelType }> = ({
  channel,
}) => {
  const { setFeedback } = useSnackbar();
  const handleCopy = () =>
    copyToClipboard(channel.code, () =>
      setFeedback({
        open: true,
        type: "default",
        message: <CopyToClipboardFeedback />,
      })
    );

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0 },
        "&, &:first-of-type, &:last-of-type": { borderRadius: "8px" },
      }}
    >
      <CouponAccordionSummary channel={channel} onCopy={handleCopy} />
      <AccordionDetails sx={{ borderTop: 1, borderColor: "divider", px: 4 }}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap={3}
          sx={{ pt: 1 }}
        >
          <DetailColumn
            label="Type:"
            value={channel.is_recurring ? "Recurring" : "One-time"}
          />
          <DetailColumn label="Applies to:" value={formatAppliesTo(channel)} />
          <DetailColumn
            label="Expiration:"
            value={formatExpiration(channel.end_date)}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const YourCouponsSection: React.FC<{
  channels?: ReferralChannelType[];
}> = ({ channels }) => {
  const fetched = useSWR<ReferralChannelType[]>(
    channels === undefined ? REFERRAL_CHANNELS_URL : null,
    { revalidateOnFocus: false }
  ).data;
  const effective = channels ?? fetched;

  if (!effective || effective.length === 0) return null;

  return (
    <Card sx={{ borderRadius: 1, p: 3 }}>
      <Typography variant="h6" color="text.primary" sx={{ mb: 3 }}>
        Your coupons
      </Typography>
      <Stack spacing={2} useFlexGap>
        {effective.map((channel) => (
          <CouponAccordion key={channel.id} channel={channel} />
        ))}
      </Stack>
    </Card>
  );
};
